import React, { useState } from "react";
import { Popover } from "antd";
import { CloseOutlined, EditOutlined, MoreOutlined, PlusOutlined, CaretRightOutlined } from "@ant-design/icons";
import { SbTag, SbButton, SbTooltip } from "uiCore";
import { getRoleChangeConfig } from "../orgDesign.constants";
import { getDescendantIds, newRoleNodeId } from "../orgDesignTreeEngine";
import { useCanViewOrgDesignBudget } from "../useOrgDesignData";
import { useCollapseScrollAnchor } from "../useCollapseScrollAnchor";
import { useOrgTreeScrollCorrection } from "../orgTreeScrollCorrectionContext";
import { SizedInputNumberStyled, TooltipTargetStyled } from "../orgDesign.styles";
import {
  BoxStyled, TitleRowStyled, StatsRowStyled, EditRowStyled, PopoverContentStyled,
  FullWidthSelectStyled, AddChildTitleInputStyled, AddChildRowStyled, RenameInputStyled,
  CollapseToggleStyled, CollapseCaretStyled, CollapsedSummaryStyled,
} from "./OrgDesignRoleBoxNode.styles";

// A collapsed subtree's status breakdown, capped so the summary line never
// wraps regardless of how many distinct statuses are hidden inside.
const MAX_SUMMARY_CATEGORIES = 3;

// The synthetic "root" wrapper buildOrgDesignResult returns is never
// editable — it has no headcount of its own. Only its `ch` are rendered, so
// this is belt-and-braces.
const isEditableNode = (nodeId) => Boolean(nodeId) && nodeId !== "root";
const isNewRoleNode = (nodeId) => Boolean(nodeId) && nodeId.startsWith("new_");

// Combined role+proposed-role list — a `newRoles` entry is a first-class
// tree node just like a real role (the tree engine folds it into the same
// buildTree recursion via its own `manager_id`, see orgDesignTreeEngine.js),
// so it can be both moved AND targeted as a destination. Excludes the node
// itself and its own descendants across both arrays (getDescendantIds walks
// manager_id links generically).
const buildReparentOptions = (roles, newRoles, nodeId) => {
  const pseudoNodes = [
    ...roles.map((r) => ({ sb_unique_id: r.sb_unique_id, manager_id: r.manager_id })),
    ...newRoles.map((nr) => ({ sb_unique_id: newRoleNodeId(nr.id), manager_id: nr.manager_id })),
  ];
  const excluded = getDescendantIds(pseudoNodes, nodeId);
  return [
    ...roles.filter((r) => !excluded.has(r.sb_unique_id)).map((r) => ({ value: r.sb_unique_id, label: r.job_title })),
    ...newRoles
      .filter((nr) => !excluded.has(newRoleNodeId(nr.id)))
      .map((nr) => ({ value: newRoleNodeId(nr.id), label: `${nr.role_title} (proposed)` })),
  ];
};

// Shown in place of a collapsed subtree so hiding it never hides the fact
// that changes exist inside — reuses getRoleChangeConfig for label/color,
// same vocabulary as the tag on every uncollapsed node and the legend.
const CollapsedSubtreeSummary = ({ summary, entryOption }) => {
  const categories = Object.entries(summary.byStatus);
  return (
    <CollapsedSummaryStyled>
      <span>{summary.total} role{summary.total === 1 ? "" : "s"}</span>
      {categories.length > 0 && categories.length <= MAX_SUMMARY_CATEGORIES ? (
        categories.map(([status, count]) => {
          const cfg = getRoleChangeConfig(status, entryOption);
          return <SbTag key={status} color={cfg.color}>{count} {cfg.label}</SbTag>;
        })
      ) : categories.length > MAX_SUMMARY_CATEGORIES ? (
        <SbTooltip
          content={categories.map(([status, count]) => `${count} ${getRoleChangeConfig(status, entryOption).label}`).join(", ")}
          trigger={["hover", "focus"]}
        >
          <SbTag color="processing" tabIndex={0}>{categories.reduce((sum, [, count]) => sum + count, 0)} changed</SbTag>
        </SbTooltip>
      ) : null}
    </CollapsedSummaryStyled>
  );
};

const OrgDesignRoleBoxNode = ({
  node, editable, onEdit, roles = [], newRoles = [],
  hasChildren = false, collapsed = false, onToggleCollapse, changeSummary, entryOption,
}) => {
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState(node.l);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [addChildDraft, setAddChildDraft] = useState({ title: "", headcount: 1, annualCost: 140000 });
  const canViewBudget = useCanViewOrgDesignBudget();
  // Keeps this node's on-screen position stable across its own collapse
  // toggle (bug #1910) — see useCollapseScrollAnchor for the mechanism and
  // orgTreeScrollCorrectionContext for why the correction has to be routed
  // through OrgTreeCard rather than written to the DOM directly.
  const scrollCorrection = useOrgTreeScrollCorrection();
  const { boxRef, handleToggle: handleToggleCollapse } = useCollapseScrollAnchor(onToggleCollapse, scrollCorrection);

  const canEdit = editable && isEditableNode(node.id);
  const isNewRole = isNewRoleNode(node.id);
  const changeConfig = getRoleChangeConfig(node.s, entryOption);

  const commitRename = () => {
    setRenaming(false);
    if (titleDraft && titleDraft !== node.l) onEdit("rename", node.id, { title: titleDraft });
  };

  const handleMove = (managerId) => {
    onEdit("move", node.id, { managerId });
    setPopoverOpen(false);
  };

  const handleAddChild = () => {
    if (!addChildDraft.title) return;
    onEdit("addChild", node.id, addChildDraft);
    setAddChildDraft({ title: "", headcount: 1, annualCost: 140000 });
    setPopoverOpen(false);
  };

  const popoverContent = (
    <PopoverContentStyled>
      <div>
        <div className="section-label">Move to…</div>
        <FullWidthSelectStyled
          size="small"
          placeholder="No manager"
          allowClear
          popupMatchSelectWidth={false}
          options={buildReparentOptions(roles, newRoles, node.id)}
          onChange={(value) => handleMove(value || null)}
        />
      </div>
      <div>
        <div className="section-label">Add Child Role</div>
        <AddChildTitleInputStyled
          size="small"
          placeholder="Role title"
          value={addChildDraft.title}
          onChange={(e) => setAddChildDraft((d) => ({ ...d, title: e.target.value }))}
        />
        <AddChildRowStyled>
          <SizedInputNumberStyled $width={70} size="small" min={1} value={addChildDraft.headcount} title="Headcount"
            onChange={(value) => setAddChildDraft((d) => ({ ...d, headcount: value }))} />
          {canViewBudget && (
            <SizedInputNumberStyled $width={130} size="small" min={0} step={5000} value={addChildDraft.annualCost}
              placeholder="Budget ($/FTE)"
              onChange={(value) => setAddChildDraft((d) => ({ ...d, annualCost: value }))} />
          )}
        </AddChildRowStyled>
        <SbTooltip content="You don't have permission to add a role without compensation data." disabled={canViewBudget}>
          <TooltipTargetStyled $block>
            <SbButton buttonType="primary" size="small" icon={<PlusOutlined />} onClick={handleAddChild} disabled={!canViewBudget} block>
              Add
            </SbButton>
          </TooltipTargetStyled>
        </SbTooltip>
      </div>
    </PopoverContentStyled>
  );

  return (
    <BoxStyled ref={boxRef} $accentToken={changeConfig.accentToken}>
      {hasChildren && (
        <CollapseToggleStyled type="button" title={collapsed ? "Expand" : "Collapse"} onClick={handleToggleCollapse}>
          <CollapseCaretStyled $collapsed={collapsed}>
            <CaretRightOutlined />
          </CollapseCaretStyled>
        </CollapseToggleStyled>
      )}
      <TitleRowStyled>
        {renaming ? (
          <RenameInputStyled
            size="small"
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onPressEnter={commitRename}
            onBlur={commitRename}
          />
        ) : (
          <>
            {node.l}
            {canEdit && !isNewRole && (
              <SbButton buttonType="action" size="small" icon={<EditOutlined />} title="Rename role" onClick={() => setRenaming(true)} />
            )}
          </>
        )}
      </TitleRowStyled>

      <StatsRowStyled>
        <span>{node.n} FTE</span>
        <SbTag color={changeConfig.color}>{changeConfig.label}</SbTag>
      </StatsRowStyled>

      {changeSummary && <CollapsedSubtreeSummary summary={changeSummary} entryOption={entryOption} />}

      {canEdit && (
        <EditRowStyled>
          <SizedInputNumberStyled
            $width={64}
            size="small"
            min={0}
            value={node.n}
            onChange={(value) => onEdit("headcount", node.id, { headcount: value })}
          />
          <Popover
            trigger="click"
            placement="bottom"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            content={popoverContent}
          >
            <SbButton buttonType="secondary" size="small" icon={<MoreOutlined />} title="Move / Add child" />
          </Popover>
          <SbButton
            buttonType="secondary"
            size="small"
            icon={<CloseOutlined />}
            title="Mark redundant"
            onClick={() => onEdit("remove", node.id, {})}
          />
        </EditRowStyled>
      )}
    </BoxStyled>
  );
};

export default OrgDesignRoleBoxNode;
